import { useSurvey } from "@hooks/useSurveySelectors";
import Analytics from "@shared/utils/tagManagerAnalytics";
import LeaseComparison from "@survey-components/comparisonPreview/LeaseComparison";
import PropertyComparison from "@survey-components/comparisonPreview/PropertyComparison";
import CoverSectionPreview from "@survey-components/coverPreview/CoverSectionPreview";
import MarketMapWrapper from "@survey-components/preview/MarketMapWrapper";
import PropertyDetailsPreview from "@survey-components/propertiesPreview/PropertyDetailsPreview";
import CustomSectionPreview from "@survey-preview/CustomSectionPreview";
import TourScheduleSectionPreview from "@survey-preview/TourScheduleSectionPreview";
import { SurveySection } from "@survey-types/survey.section.types";
import { useEnsureMediaData } from "@survey/components/preview/hooks/useEnsureMediaData";
import { isUSUser } from "@utils/countryUtils";
import { ReactNode, useEffect } from "react";
import { useParams } from "react-router";

const BuilderMain = () => {
  const survey = useSurvey();
  const { surveyId } = useParams();
  // Use store-first architecture with selectors and ensure media data is fetched
  useEnsureMediaData(); // Single API call for all media data
  // Send analytics event when survey loads
  useEffect(() => {
    if (survey?.id !== undefined) {
      Analytics.send({
        event: "userDataReady",
        user_id: localStorage.getItem("employeeId"),
        jobTitle: localStorage.getItem("position"),
        officeName: localStorage.getItem("office"),
        email: localStorage.getItem("email"),
        functUnitName: localStorage.getItem("functUnitName"),
        SiteIQUserID: survey?.createdBy || null, // Use createdBy as fallback since user property doesn't exist
        teamName: survey?.teamName, // team property not available on DetailedSurvey type
      });
    }
  }, [survey?.id, survey?.createdBy, survey?.teamName]);

  // Helper to wrap section content in consistent container
  const renderSectionWrapper = (section: SurveySection, content: ReactNode) => (
    <div
      key={section.surveySectionId}
      data-section-id={section.surveySectionId}
      className="flex flex-col gap-y-4"
    >
      {content}
    </div>
  );

  // Mapping of section names to their components
  const sectionComponents: Record<string, ReactNode> = {
    cover: <CoverSectionPreview mode="PDFPreview" />,
    properties: <PropertyDetailsPreview mode="PDFPreview" />,
    marketmap: <MarketMapWrapper mode="PDFPreview" />,
    tourschedule: <TourScheduleSectionPreview mode="PDFPreview" />,
    propertycomparison: <PropertyComparison />,
    ...(isUSUser() && { leasecomps: <LeaseComparison /> }),
  };

  const displaySurveySections = () => {
    if (!survey?.sections) {
      return <p className="text-gray-500">No sections available.</p>;
    }

    return survey.sections.map((section: SurveySection) => {
      if (section.isHidden) return null;

      // Handle custom sections
      if (section.isCustom) {
        return renderSectionWrapper(
          section,
          <CustomSectionPreview section={section} mode="PDFPreview" />,
        );
      }

      // Handle predefined sections
      const sectionName = section.name
        ?.replace(/\s/g, "")
        .toLowerCase()
        ?.trim();
      const component = sectionComponents[sectionName];

      return component ? renderSectionWrapper(section, component) : null;
    });
  };

  return (
    <div className="builder-main overflow-y-auto scroll-smooth">
      {survey && survey.id === surveyId ? (
        <div className="flex flex-col items-center py-4 gap-y-4">
          {displaySurveySections()}
        </div>
      ) : (
        <div className="text-gray-500 text-center py-6">
          Loading survey data...
        </div>
      )}
    </div>
  );
};

export default BuilderMain;
